import {
  memo,
  useState,
  useCallback,
  useContext,
  useMemo,
  useEffect
} from 'react'

import TtsTextArea from 'containers/SubSidebar/shared/Voices/TextArea'

import { TtsItemContext } from '../contextApi'

import * as Styled from './styled'
import {
  TTS_TEXT_AREA_ACTIVE_MIN_HEIGHT,
  TTS_TEXT_AREA_INITIAL_HEIGHT
} from '../consts'
import {
  useGetInitialTtsItemText,
  useGetTtsItemDraftState,
  useGetTtsItemGeneratedState,
  useGetTtsItemLoadingState
} from 'containers/SubSidebar/TtsContent/hooks'
import { dispatch } from 'store'
import { ttsThunks } from 'store/features/tts/thunks'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { useResponsive } from 'hooks/useResponsive'
import debounce from 'lodash.debounce'

interface IProps {
  isActive: boolean
}

const TtsItemText: React.FC<IProps> = ({ isActive }: IProps) => {
  const { id, text } = useContext(TtsItemContext)
  const projectInitialTtsTextValue = useGetInitialTtsItemText(id)
  const [textValue, setTextValue] = useState<string>(text)
  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const isLoading = useGetTtsItemLoadingState(id)
  const isGenerated = useGetTtsItemGeneratedState(id)
  const isDraft = useGetTtsItemDraftState(id)

  useEffect(() => {
    setTextValue(text)
  }, [text])

  const handleTextBlurAction = useCallback(
    (newValue: string) => {
      dispatch(ttsThunks.setTtsItemText(id, newValue))
    },
    [id]
  )

  const debouncedTextChange = useMemo(
    () =>
      debounce((newValue: string) => {
        dispatch(ttsThunks.setTtsItemText(id, newValue))
        dispatch(ttsThunks.checkGeneratedStateByText(id, newValue))
      }, 500),
    [id]
  )

  const handleTextChangeAction = useCallback(
    (newValue: string) => {
      setTextValue(newValue)
      debouncedTextChange(newValue)
    },
    [debouncedTextChange]
  )

  return (
    <Styled.TtsItemTextAreaWrapper>
      <TtsTextArea
        isActive={isActive}
        textValue={textValue}
        mutableTtsTextValue={textValue}
        initialTtsTextValue={projectInitialTtsTextValue}
        setTextValue={setTextValue}
        handleTextBlurAction={handleTextBlurAction}
        handleTextChangeAction={handleTextChangeAction}
        isDefaultStateFocused={isActive && !isTablet}
        inputFixHeight={isActive ? undefined : TTS_TEXT_AREA_INITIAL_HEIGHT}
        minHeight={TTS_TEXT_AREA_ACTIVE_MIN_HEIGHT}
        isLoading={isLoading}
        isGenerated={isGenerated}
        isDraft={isDraft}
      />
    </Styled.TtsItemTextAreaWrapper>
  )
}

export default memo(TtsItemText)
